// MortgageMarketingROICalculator.config.ts
// Configuration and field definitions for the calculator

import { FieldConfig, CalculatorInputs, PresetConfig } from './calculator.types';

// Type exports for main component
export type FieldKey = keyof CalculatorInputs;
export type PresetKey = 'average' | 'optimized' | 'best_in_class' | 'custom';

export interface MortgageMarketingROICalculatorProps {
  onStateChange?: (state: any, outputs: any) => void;
  onShare?: (url: string) => void;
  initialPreset?: PresetKey;
  showSocialProof?: boolean;
  className?: string;
  onSwitchToV1?: () => void;
  onSwitchToV3?: () => void;
  onSwitchToV4?: () => void;
}

// Field configurations with CORRECTED ranges
export const defaultFieldConfigs: Record<FieldKey, FieldConfig> = {
  adSpend: {
    type: 'currency',
    label: 'Ad Spend',
    min: 500,
    max: 100000,
    defaultValue: 2500,
    step: 500,
    sliderScale: 'logarithmic',
    tooltip: 'Enter your total monthly ad budget.\n\nMost successful LOs start with a test budget of $1,500-$3,000 per month to establish a baseline ROI, then scale aggressively once profitability is proven.',
    contextHint: '',
    displayFormat: 'currency',
    decimalPlaces: 0,
    effect: 'Higher spend can mean more leads, but efficiency matters more'
  },
  cpc: {
    type: 'currency',
    label: 'Cost Per Click',
    min: 5,
    max: 50, // FIXED: Was 150
    defaultValue: 15,
    step: 1,
    tooltip: 'Your average CPC. In mortgage, this can range from $8-$25 for high-intent keywords.\n\nA high CPC isn\'t bad if your conversion rate is high.',
    contextHint: '',
    displayFormat: 'currency',
    decimalPlaces: 0,
    effect: 'Lower CPC = more visitors for same budget'
  },
  landingPageConv: {
    type: 'percentage',
    label: 'Landing Page Conversion',
    min: 2, // FIXED: Was 0.5
    max: 80, // Updated to 80% max
    defaultValue: 8,
    step: 1, // FIXED: Was 0.1
    tooltip: 'The percentage of ad clicks that become leads.\n\nPoint ads at a typical loan officer website and under 2% of clicks become leads. A well-optimized landing page turns 25-35% of visitors into leads.',
    contextHint: '',
    displayFormat: 'percentage',
    decimalPlaces: 0,
    effect: 'Each 1% improvement can dramatically increase ROI'
  },
  leadContactRate: {
    type: 'percentage',
    label: 'Lead Contact Rate',
    min: 10, // FIXED: Was 5
    max: 80, // FIXED: Was 50
    defaultValue: 25,
    step: 5, // FIXED: Was 1
    tooltip: 'What percentage of your leads do you actually speak with?\n\nThe industry average is a painful 25%. Our instant response system pushes this to 65%+.',
    contextHint: '',
    displayFormat: 'percentage',
    decimalPlaces: 0,
    effect: 'Direct contact rate without modifiers'
  },
  avgCommission: {
    type: 'currency',
    label: 'Average Commission',
    min: 2000, // FIXED: Was 3000
    max: 10000, // FIXED: Was 30000
    defaultValue: 4000,
    step: 250,
    tooltip: 'Your average commission per closed loan. A typical range is $3,500-$5,000.\n\nBe honest—this is the key to your final ROI.',
    contextHint: '',
    displayFormat: 'currency',
    decimalPlaces: 0,
    effect: 'Higher commissions justify higher acquisition costs'
  },
  contactToApp: {
    type: 'percentage',
    label: 'Contact to Application',
    min: 1,
    max: 30, // FIXED: Was 80
    defaultValue: 8,
    step: 0.5, // FIXED: Was 5
    tooltip: 'Of the leads you speak with, how many submit a full application?\n\nThis reflects your sales process. Top performers hit 10-15%.',
    contextHint: '',
    displayFormat: 'percentage',
    decimalPlaces: 1,
    effect: 'Sales skills and qualification quality'
  },
  appToClose: {
    type: 'percentage',
    label: 'Application to Close',
    min: 20, // FIXED: Was 10
    max: 90,
    defaultValue: 30,
    step: 5, // FIXED
    tooltip: 'Of the applications you take, how many actually fund?\n\nA strong pre-qualification process pushes this number above 60%.',
    contextHint: '',
    displayFormat: 'percentage',
    decimalPlaces: 0,
    effect: 'Better qualification improves this metric'
  },
  agentReferrals: {
    type: 'number',
    label: 'Agent Referral Loans (per month)',
    min: 0,
    max: 20,
    defaultValue: 0,
    step: 1,
    tooltip: 'Enter your average monthly closings from agent referrals.\n\nThis is your highest-converting lead source (agent referrals close at 40-60%) but often the most inconsistent.',
    contextHint: '',
    displayFormat: 'number',
    decimalPlaces: 0,
    effect: 'Free loans that multiply your ROI'
  },
  clientReferrals: {
    type: 'number',
    label: 'Client Referral Loans (per month)',
    min: 0,
    max: 10, // FIXED: Was 20
    defaultValue: 0,
    step: 1,
    tooltip: 'Enter your average monthly closings from past clients.\n\nA systematic approach can turn every closed loan into a future referral.',
    contextHint: '',
    displayFormat: 'number',
    decimalPlaces: 0,
    effect: 'The best leads come from happy clients'
  },
  databaseReactivation: {
    type: 'number',
    label: 'Database/Nurture Loans (per month)',
    min: 0,
    max: 10,
    defaultValue: 2,
    step: 1,
    tooltip: 'How many additional loans do you close from nurturing your past client database and long-term leads?\n\nProper nurturing of your database typically generates 1-3 additional loans monthly.',
    contextHint: '',
    displayFormat: 'number',
    decimalPlaces: 0,
    effect: 'Long-term nurturing pays off'
  },
  organicLoans: {
    type: 'number',
    label: 'Organic Website Loans (per month)',
    min: 0,
    max: 50,
    defaultValue: 0,
    step: 1,
    tooltip: 'Enter your average monthly closed loans that originated from organic search (Google) or direct traffic to your website.\n\nA high-converting website (one that turns 5-15% of visitors into leads) is the key to turning this into a powerful, low-cost source of business.',
    contextHint: '',
    displayFormat: 'number',
    decimalPlaces: 0,
    effect: 'SEO and brand traffic are long-term assets'
  }
};

// CORRECTED Preset configurations
export const presetConfigurations: Record<PresetKey, { name: string; description: string; inputs: CalculatorInputs }> = {
  average: {
    name: 'Broken System',
    description: 'Industry average - losing money',
    inputs: {
      adSpend: 2500,
      cpc: 15,
      landingPageConv: 8,
      leadContactRate: 25,
      avgCommission: 4000,
      contactToApp: 8,
      appToClose: 30,
      agentReferrals: 1,
      clientReferrals: 0,
      databaseReactivation: 0,
      organicLoans: 0
    }
  },
  optimized: {
    name: 'Basic Optimization',
    description: 'Foundation improvements - profitable',
    inputs: {
      adSpend: 2500,
      cpc: 12,
      landingPageConv: 18,
      leadContactRate: 50,
      avgCommission: 4000,
      contactToApp: 13.3,
      appToClose: 50,
      agentReferrals: 2,
      clientReferrals: 1,
      databaseReactivation: 2,
      organicLoans: 1
    }
  },
  best_in_class: {
    name: 'Full Optimization',
    description: 'Complete system - maximized ROI',
    inputs: {
      adSpend: 2500,
      cpc: 10,
      landingPageConv: 25,
      leadContactRate: 65,
      avgCommission: 4000,
      contactToApp: 12.5,
      appToClose: 50,
      agentReferrals: 4,
      clientReferrals: 2,
      databaseReactivation: 3,
      organicLoans: 2
    }
  },
  custom: {
    name: 'Custom',
    description: 'Your custom configuration',
    inputs: {
      adSpend: 2500,
      cpc: 15,
      landingPageConv: 8,
      leadContactRate: 25,
      avgCommission: 4000,
      contactToApp: 8,
      appToClose: 30,
      agentReferrals: 0,
      clientReferrals: 0,
      databaseReactivation: 0,
      organicLoans: 0
    }
  }
};